import {
    Box,
    Divider,
    Flex,
    Text,
    useMultiStyleConfig,
} from '@salesforce/retail-react-app/app/components/shared/ui'
import PropTypes from 'prop-types'
import React from 'react'
import ProductTileImage from '../../product-tile/partials/product-tile-image'
import DisplayPrice from '../../display-price'
import {useCurrency} from '@salesforce/retail-react-app/app/hooks'
import {PlusIcon} from '../../icons'
import {FormattedMessage} from 'react-intl'

import {getPriceData} from '../../../utils/product-utils'
import {useGoogleTagManager} from '../../../custom-api/hooks/use-google-tag-manager'
import {item_gtm_event} from '../../../custom-api/layers/default'
import {useBooleanCustomPreference} from '../../../hooks/use-custom-preference'

const ProductTileBundle = ({product, firstElement, lastElement, productIndex = undefined}) => {
    const styles = useMultiStyleConfig('Bundle')
    const {currency} = useCurrency()
    const {addLayer} = useGoogleTagManager()
    const notShoppableCountry = useBooleanCustomPreference('notShoppableCountry')

    const discount = product.discountPercentage !== '0%'
    const freeGift = product.discountPercentage === '100%'

    // Replaces the individual product price with the new price in the bundle
    const bundlePriceData = {
        listPrice: product.standardPrice,
        currentPrice: product.price,
        isOnSale: discount,
    }

    const selectItemTileEventGtm = async () => {
        await addLayer(
            item_gtm_event.bind(this, {
                event: 'select_item',
                item_list_name: 'carousel_pdp',
                items: [
                    {
                        item_name: product?.c_defaultName,
                        item_id: product?.c_variants?.[0]?.productId,
                        item_category: product?.c_defaultCategoryName,
                        item_variant: product?.id,
                        price: +(product?.price / (1 + product?.c_taxRate / 100)).toFixed(2),
                        fullprice: getPriceData(product)?.isOnSale ? 'reduced' : 'fullprice',
                        rating_points: '',
                        // get_reevoo_rating() || get_bv_rating(),
                        erp_category: product?.c_materialGroup,
                        prdha_category: product?.c_prdha_category,
                        index: productIndex + 1,
                        stock_status:
                            product?.inventory?.stockLevel > 0 ? 'in-stock' : 'out-of-stock',
                    },
                ],
            }),
        )
    }

    return (
        <>
            <Flex
                {...styles?.bundleCard}
                {...(firstElement ? styles?.firstBundleCard : {})}
                data-testid="bundle-product-card"
            >
                <Flex {...styles?.bundleImageContainer} data-testid="bundle-product-image">
                    <ProductTileImage
                        product={product}
                        enableFavourite={false}
                        variant={'bundle'}
                        height={styles?.productTileImage?.height}
                        priceData={bundlePriceData}
                        customImage={product.image}
                        selectItemTileEventGtm={selectItemTileEventGtm}
                        bundleFirstElement={firstElement}
                        imageStyle={styles?.productTileImage}
                    />
                </Flex>

                <Flex {...styles?.productDetailsContainer}>
                    {/* This discount label doesn't render on mobile due to display none in styles */}
                    {discount && !freeGift && (
                        <Box {...styles?.bundleDiscountLabel} data-testid="bundle-discount-label">
                            <Text>-{product.discountPercentage}</Text>
                        </Box>
                    )}

                    <Box {...styles?.productDetailNameContainer}>
                        <Text {...styles?.productDetailName}>{product.productName}</Text>
                    </Box>

                    {freeGift ? (
                        <Box {...styles?.bundleFreeGiftLabel}>
                            <FormattedMessage
                                defaultMessage="Free Gift"
                                id="bundle_free_gift.text"
                            />
                        </Box>
                    ) : (
                        <Flex {...styles?.discountAndPriceContainer}>
                            {/* This discount label doesn't render on desktop due to display none in styles */}
                            {discount && !freeGift && (
                                <Box
                                    {...styles?.bundleDiscountLabelMobile}
                                    data-testid="bundle-discount-label"
                                >
                                    <Text>-{product.discountPercentage}</Text>
                                </Box>
                            )}
                            <DisplayPrice
                                priceData={bundlePriceData}
                                currency={currency}
                                listPriceProps={{...styles?.productListPrice}}
                                currentPriceProps={{...styles?.productDetailPrice}}
                                priceContainerProps={{
                                    ...styles?.productPriceContainer,
                                }}
                                discountProps={{display: 'none'}}
                                productDetailPriceView={true}
                                notShoppableCountry={notShoppableCountry}
                            />
                        </Flex>
                    )}
                </Flex>
            </Flex>
            {!lastElement ? (
                <Box {...styles?.plusIconContainer}>
                    <PlusIcon {...styles?.plusIcon} />
                </Box>
            ) : (
                <Divider {...styles.divider} />
            )}
        </>
    )
}

ProductTileBundle.propTypes = {
    variant: PropTypes.string,
    product: PropTypes.object,
    firstElement: PropTypes.bool,
    lastElement: PropTypes.bool,
    productIndex: PropTypes.number,
}

export default ProductTileBundle
